import { useState, useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  Badge,
  Dropdown,
  Option,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Textarea,
  Field,
} from '@fluentui/react-components';
import {
  ChevronLeft24Regular,
  ChevronRight24Regular,
  Edit24Regular,
  Chat24Regular,
} from '@fluentui/react-icons';
import dayjs from 'dayjs';
import { useAuth } from '@/components/auth/AuthProvider';
import { useMyReferrals, useMyCommissions, useUpdateReferrerNotes, useComments } from '@/api/hooks/useReferrals';
import { StatusBadge } from '@/components/referral/StatusBadge';
import { CommentThread } from '@/components/shared/CommentThread';
import { REFERRAL_STATUSES, STATUS_LABELS, COMMISSION_TYPE_LABELS, PAYOUT_STATUS_LABELS } from '@/config/services';
import type { ReferralStatus } from '@/config/services';
import type { ReferralDto, CommissionDto } from '@/types/referral';

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
  },
  heading: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow4,
    overflow: 'hidden',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  serviceList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  emptyState: {
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  notesButton: {
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    maxWidth: '200px',
    transitionDuration: tokens.durationNormal,
    transitionProperty: 'background',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  notesEditIcon: {
    color: tokens.colorNeutralForeground3,
    fontSize: '16px',
    flexShrink: 0,
  },
  notesText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

// ─── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/referrals/mine')({
  component: MyReferralsPage,
});

// ─── Notes edit dialog ───────────────────────────────────────────────────────

interface NotesEditDialogProps {
  referral: ReferralDto;
  email: string;
  open: boolean;
  onClose: () => void;
}

function NotesEditDialog({ referral, email, open, onClose }: NotesEditDialogProps) {
  const updateNotes = useUpdateReferrerNotes();
  const [notes, setNotes] = useState(referral.referrerComments ?? '');
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaveError(null);
    try {
      await updateNotes.mutateAsync({
        id: referral.id,
        email,
        data: { referrerComments: notes.trim() },
      });
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to update notes.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_e, d) => { if (!d.open && !updateNotes.isPending) onClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Edit My Notes</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
              <Text size={300}>
                <strong>{referral.clientContactName}</strong>
                {referral.businessName ? ` — ${referral.businessName}` : ''}
              </Text>

              {saveError && (
                <MessageBar intent="error">
                  <MessageBarBody>{saveError}</MessageBarBody>
                </MessageBar>
              )}

              <Field label="My Notes">
                <Textarea
                  value={notes}
                  onChange={(_e, d) => setNotes(d.value)}
                  placeholder="Add any notes about this referral..."
                  rows={4}
                />
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={updateNotes.isPending}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={() => void handleSave()}
              disabled={updateNotes.isPending}
              icon={updateNotes.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {updateNotes.isPending ? 'Saving...' : 'Save Notes'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

function MyReferralsPage() {
  const styles = useStyles();
  const { userEmail, userName } = useAuth();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<ReferralStatus | ''>('');
  const PAGE_SIZE = 15;

  const { data, isLoading, isError, error } = useMyReferrals(userEmail ?? '', {
    page,
    pageSize: PAGE_SIZE,
    status: statusFilter,
  });

  const { data: allCommissions } = useMyCommissions(userEmail ?? '');

  const commissionsByReferralId = useMemo(() => {
    const map = new Map<number, CommissionDto[]>();
    if (allCommissions) {
      for (const c of allCommissions) {
        const list = map.get(c.referralId) ?? [];
        list.push(c);
        map.set(c.referralId, list);
      }
    }
    return map;
  }, [allCommissions]);

  const referrals = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <Text className={styles.heading} block>
            My Referrals
          </Text>
          <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
            {userEmail ? `Showing referrals submitted by ${userEmail}` : 'Loading...'}
          </Text>
        </div>

        <div className={styles.toolbar}>
          <Dropdown
            placeholder="Filter by status"
            value={statusFilter ? STATUS_LABELS[statusFilter] : ''}
            selectedOptions={statusFilter ? [statusFilter] : []}
            onOptionSelect={(_e, d) => {
              setStatusFilter((d.optionValue as ReferralStatus | '') ?? '');
              setPage(1);
            }}
            style={{ minWidth: '160px' }}
          >
            <Option value="" text="All Statuses">
              All Statuses
            </Option>
            {REFERRAL_STATUSES.map((s) => (
              <Option key={s} value={s}>
                {STATUS_LABELS[s]}
              </Option>
            ))}
          </Dropdown>
        </div>
      </div>

      {/* Error state */}
      {isError && (
        <MessageBar intent="error">
          <MessageBarBody>
            {error instanceof Error ? error.message : 'Failed to load referrals.'}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Loading state */}
      {isLoading && (
        <div style={{ padding: tokens.spacingVerticalXXL, textAlign: 'center' }}>
          <Spinner label="Loading your referrals..." />
        </div>
      )}

      {/* Table */}
      {!isLoading && (
        <div className={styles.card}>
          <div className={styles.tableWrapper}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Date Submitted</TableHeaderCell>
                  <TableHeaderCell>Business Name</TableHeaderCell>
                  <TableHeaderCell>Client Contact</TableHeaderCell>
                  <TableHeaderCell>Services</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Commission</TableHeaderCell>
                  <TableHeaderCell>My Notes</TableHeaderCell>
                  <TableHeaderCell>Comments</TableHeaderCell>
                  <TableHeaderCell>Status Notes</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referrals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <div className={styles.emptyState}>
                        <Text>No referrals found.</Text>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  referrals.map((r) => (
                    <ReferralRow
                      key={r.id}
                      referral={r}
                      email={userEmail ?? ''}
                      userName={userName ?? ''}
                      commissions={commissionsByReferralId.get(r.id) ?? []}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className={styles.pagination}>
            <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
              {totalCount > 0
                ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, totalCount)} of ${totalCount}`
                : 'No results'}
            </Text>
            <div style={{ display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center' }}>
              <Button
                appearance="subtle"
                icon={<ChevronLeft24Regular />}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              />
              <Text size={200}>
                Page {page} of {totalPages}
              </Text>
              <Button
                appearance="subtle"
                icon={<ChevronRight24Regular />}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="Next page"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Row sub-component ────────────────────────────────────────────────────────

function ReferralRow({ referral, email, userName, commissions }: { referral: ReferralDto; email: string; userName: string; commissions: CommissionDto[] }) {
  const styles = useStyles();
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const { data: comments } = useComments(referral.id);
  const commentCount = comments?.length ?? 0;

  return (
    <>
      <TableRow>
        <TableCell>
          <TableCellLayout>{dayjs(referral.createdAt).format('MMM D, YYYY')}</TableCellLayout>
        </TableCell>
        <TableCell>
          <TableCellLayout>{referral.businessName ?? '—'}</TableCellLayout>
        </TableCell>
        <TableCell>
          <TableCellLayout>
            <div>
              <Text size={300} weight="semibold" block>
                {referral.clientContactName}
              </Text>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                {referral.clientEmail}
              </Text>
            </div>
          </TableCellLayout>
        </TableCell>
        <TableCell>
          <TableCellLayout>
            <div className={styles.serviceList}>
              {referral.services.slice(0, 2).map((svc) => (
                <Badge key={svc} appearance="tint" color="informative" size="small">
                  {svc}
                </Badge>
              ))}
              {referral.services.length > 2 && (
                <Badge appearance="tint" color="subtle" size="small">
                  +{referral.services.length - 2}
                </Badge>
              )}
            </div>
          </TableCellLayout>
        </TableCell>
        <TableCell>
          <TableCellLayout>
            <StatusBadge status={referral.status} />
          </TableCellLayout>
        </TableCell>
        <TableCell>
          <TableCellLayout>
            {commissions.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Text size={300} weight="semibold" style={{ color: tokens.colorPaletteGreenForeground1 }}>
                  ${commissions.reduce((sum, c) => sum + c.amount, 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Text>
                {commissions.map((c) => (
                  <Text key={c.id} size={100} style={{ color: tokens.colorNeutralForeground3 }}>
                    {COMMISSION_TYPE_LABELS[c.type]}: ${c.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} ({PAYOUT_STATUS_LABELS[c.payoutStatus]})
                  </Text>
                ))}
              </div>
            ) : (
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>—</Text>
            )}
          </TableCellLayout>
        </TableCell>
        <TableCell>
          <TableCellLayout>
            <button
              type="button"
              className={styles.notesButton}
              onClick={() => setNotesDialogOpen(true)}
              aria-label={`Edit notes for ${referral.clientContactName}`}
              title="Click to edit notes"
            >
              <Text
                size={200}
                className={styles.notesText}
                style={{ color: referral.referrerComments ? tokens.colorNeutralForeground2 : tokens.colorNeutralForeground3 }}
              >
                {referral.referrerComments || 'Add notes...'}
              </Text>
              <Edit24Regular className={styles.notesEditIcon} />
            </button>
          </TableCellLayout>
        </TableCell>
        <TableCell>
          <TableCellLayout>
            <Button
              appearance="subtle"
              size="small"
              icon={<Chat24Regular />}
              onClick={() => setCommentsDialogOpen(true)}
            >
              {commentCount > 0 ? `${commentCount}` : ''}
            </Button>
          </TableCellLayout>
        </TableCell>
        <TableCell>
          <TableCellLayout>
            <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
              {referral.statusComments ?? '—'}
            </Text>
          </TableCellLayout>
        </TableCell>
      </TableRow>

      {commentsDialogOpen && (
        <Dialog open={commentsDialogOpen} onOpenChange={(_e, d) => { if (!d.open) setCommentsDialogOpen(false); }}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>
                Comments — {referral.clientContactName}
              </DialogTitle>
              <DialogContent>
                <CommentThread
                  referralId={referral.id}
                  authorEmail={email}
                  authorName={userName}
                />
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={() => setCommentsDialogOpen(false)}>
                  Close
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}

      {notesDialogOpen && (
        <NotesEditDialog
          referral={referral}
          email={email}
          open={notesDialogOpen}
          onClose={() => setNotesDialogOpen(false)}
        />
      )}
    </>
  );
}
